import { create } from 'zustand';
import { logger } from '../helpers/logger';
import {
  ApacBpaApiPayload,
  ApacBpaApiPrescriptionPayload,
  ApacBpaExamTypeKey,
  ApacBpaPatientFormType,
  ApacBpaPrescriptionExamFromType,
  ApacBpaPrescriptionFormType,
  ApacBpaProfessionalFormType,
  CID10_TYPES,
} from '../containers/apac-bpa/Forms/helpers';
import { DOCUMENT_TYPE } from '../containers/LME/helpers';
import { DateTime } from 'luxon';
import { formatCEP, formatCNS, formatCpf, formatSigtap, onlyDigits } from '../helpers/string';
import { PrescriptionService } from '../containers/prescription/service';
import { SimpleDateFormat } from '../helpers/Date';

export interface ApacBpaStoreProps {
  formPayload: ApacBpaApiPayload | null;
  completedForm: boolean;
  patientForm: ApacBpaPatientFormType;
  professionalForm: ApacBpaProfessionalFormType;
  prescriptionForm: ApacBpaPrescriptionFormType;
  setApacBpaForms: (payload: ApacBpaApiPayload) => void;
  updatePatientForm: (prescriptionId: number, updateData: ApacBpaPatientFormType) => void;
  updateProfessionalForm: (prescriptionId: number, updateData: ApacBpaProfessionalFormType) => void;
  updatePrescriptionForm: (
    prescriptionId: number,
    updateData: ApacBpaPrescriptionExamFromType,
    position: number
  ) => void;
  updateCompletedForm(prescriptionId: number): Promise<ApacBpaApiPayload | null>;
  resetApacBpaForm: () => void;
}

const GET_EMPTY_STORE = () => ({
  formPayload: null,
  completedForm: false,
  patientForm: {
    patientName: '',
    parentName: '',
    parentPhone: '',
    ethnicity: undefined,
    personInChargeName: '',
    personInChargePhone: '',
    patientDocNumber: '',
    ibgeCityCode: '',
    patiendBirthdate: '',
    patientAddress: '',
    patientCep: '',
    patientCity: '',
    patientGender: '',
    patientUf: '',
    medicalRecordNumber: undefined,
    parentNameCapacityCertificate: '',
    patientRace: undefined,
  },
  professionalForm: {
    establishmentName: '',
    cnesNumber: '',
    professionalName: '',
    docType: undefined,
    professionalDocNumber: '',
    docDate: '',
  },
  prescriptionForm: {
    exams: [],
  },
});

export const useApacBpaStore = create<ApacBpaStoreProps>()((set, get) => ({
  ...GET_EMPTY_STORE(),
  setApacBpaForms: (payload: ApacBpaApiPayload) => {
    const patient = payload?.exameFormularioPaciente;
    const professional = payload?.exameFormularioMedico;
    const establishment = payload?.exameFormularioEstabelecimento;
    const prescription = payload?.exameFormularioPrescricao;

    const convertIsoDate = (date: string) => {
      let dateToFormat = date;
      if (date.includes('Z')) dateToFormat = date.slice(0, 19);
      return SimpleDateFormat(dateToFormat, "yyyy-MM-dd'T'HH:mm:ss");
    };

    set({
      formPayload: payload,
      patientForm: {
        ibgeCityCode: patient.endereco.codigoIbgeMunicipio,
        parentName: patient.nomeMae,
        parentPhone: patient.telefoneMae ?? '',
        patiendBirthdate: convertIsoDate(patient.dataNascimento.toString()), // Talvez nao seja a melhor abordagem.
        patientAddress: patient.endereco.endereco,
        patientCep: formatCEP(patient.endereco.cep),
        patientCity: patient.endereco.municipioDeResidencia,
        patientDocNumber: formatCNS(patient.cns),
        patientGender: patient.sexo,
        patientName: patient.nome,
        patientUf: patient.endereco.uf,
        patientRace: patient.racaCor,
        ethnicity: patient.etnia,
        medicalRecordNumber: patient.numeroProntuario,
        personInChargeName: patient.nomeResponsavel,
        personInChargePhone: patient.telefoneResponsavel ?? '',
      },
      professionalForm: {
        cnesNumber: establishment.cnes,
        docDate: DateTime.fromISO(professional.dataDaSolicitacao).toFormat('dd/MM/yyyy'),
        establishmentName: establishment.nome,
        professionalDocNumber:
          professional.tipoDocumento === DOCUMENT_TYPE.CNS
            ? formatCNS(professional.documento)
            : formatCpf(professional.documento || ''),
        professionalName: professional.nome,
        docType: professional.tipoDocumento as DOCUMENT_TYPE,
      },
      prescriptionForm: {
        exams: prescription.map((exam) => ({
          formId: exam.idPrescricaoExameFormulario,
          formType: {
            id: exam.tipoExameFormulario.idTipoExameFormulario,
            key: exam.tipoExameFormulario.Chave as ApacBpaExamTypeKey,
            name: exam.tipoExameFormulario.Nome,
          },
          CID10: exam.cid
            ? {
                description: exam.cid.descricao,
                id: exam.cid.id,
                subcategory: exam.cid.subCategoria,
              }
            : null,
          CID10Related: exam.cidAssociado
            ? {
                description: exam.cidAssociado.descricao,
                id: exam.cidAssociado.id,
                subcategory: exam.cidAssociado.subCategoria,
              }
            : null,
          CID10Secondary: exam.cidSecundario
            ? {
                description: exam.cidSecundario.descricao,
                id: exam.cidSecundario.id,
                subcategory: exam.cidSecundario.subCategoria,
              }
            : null,
          examAnamnese: exam.anamnese,
          examJustify: exam.exameJustificativaObservacao,
          examDescription: exam.cid ? exam.cid.descricao : '',
          procedure: {
            code: formatSigtap(exam.codigoExame),
            name: exam.nome,
            quantity: exam.quantidade || 1,
          },
          secondaryProcedures: exam.procedimentosSecundarios.map((procedure) => ({
            code: formatSigtap(procedure.codigoExame),
            name: procedure.nome,
            quantity: procedure.quantidade || 1,
          })),
        })),
      },
    });
  },
  updatePatientForm: (prescriptionId: number, updateData: ApacBpaPatientFormType) => {
    set((prev) => {
      try {
        const data = {
          ...prev.formPayload,
          exameFormularioPaciente: {
            ...prev.formPayload?.exameFormularioPaciente,
            cns: onlyDigits(updateData.patientDocNumber),
            dataNascimento: DateTime.fromFormat(updateData.patiendBirthdate, 'dd/MM/yyyy').toISO(),
            endereco: {
              cep: onlyDigits(updateData.patientCep),
              codigoIbgeMunicipio: updateData.ibgeCityCode,
              endereco: updateData.patientAddress,
              municipioDeResidencia: updateData.patientCity,
              uf: updateData.patientUf,
            },
            etnia: updateData.ethnicity,
            nome: updateData.patientName,
            nomeMae: updateData.parentName,
            telefoneMae: updateData.parentPhone ?? '',
            nomeResponsavel: updateData.personInChargeName,
            telefoneResponsavel: updateData.personInChargePhone ?? '',
            numeroProntuario: updateData.medicalRecordNumber,
            racaCor: updateData.patientRace,
            sexo: updateData.patientGender,
          },
        } as ApacBpaApiPayload;

        const newData = { ...prev, formPayload: { ...data }, patientForm: updateData };
        // @todo assim que corrigir a persistencia de step por step na API voltamos com essa chamada
        // PrescriptionService.updateApacBpaForm(prescriptionId, newData.formPayload);

        return newData;
      } catch (error) {
        logger.error(error);
        return prev;
      }
    });
  },
  updateProfessionalForm: (prescriptionId: number, updateData: ApacBpaProfessionalFormType) => {
    set((prev) => {
      try {
        const prevProfessionalForm = prev.formPayload?.exameFormularioMedico;
        const prevEstablishmentForm = prev.formPayload?.exameFormularioEstabelecimento;

        const data = {
          ...prev.formPayload,
          exameFormularioMedico: {
            idPrescricaoExameFormularioMedico: prevProfessionalForm?.idPrescricaoExameFormularioMedico,
            dataDaSolicitacao: DateTime.fromFormat(updateData.docDate, 'dd/MM/yyyy').toISO(),
            documento: onlyDigits(updateData.professionalDocNumber),
            nome: updateData.professionalName,
            tipoDocumento: updateData.docType,
          },
          exameFormularioEstabelecimento: {
            idPrescricaoExameFormularioEstabelecimento:
              prevEstablishmentForm?.idPrescricaoExameFormularioEstabelecimento,
            cnes: updateData.cnesNumber,
            nome: updateData.establishmentName,
          },
        } as ApacBpaApiPayload;

        const newData = { ...prev, formPayload: { ...data }, professionalForm: updateData };
        // @todo assim que corrigir a persistencia de step por step na API voltamos com essa chamada
        // PrescriptionService.updateApacBpaForm(prescriptionId, newData.formPayload);

        return newData;
      } catch (error) {
        logger.error(error);
        return prev;
      }
    });
  },

  updatePrescriptionForm: (prescriptionId: number, updateData: ApacBpaPrescriptionExamFromType, position: number) => {
    set((prev) => {
      try {
        const prevForm = prev.prescriptionForm;
        prevForm.exams[position] = updateData;

        const actualExam = prevForm.exams[position];

        const prevPayloadExam = prev.formPayload?.exameFormularioPrescricao || [];

        const primaryIcd = updateData[CID10_TYPES.primary];
        const relatedIcd = updateData[CID10_TYPES.related];
        const secondaryIcd = updateData[CID10_TYPES.related];

        const data = {
          idPrescricaoExameFormulario: actualExam.formId,
          anamnese: updateData.examAnamnese,
          cid: primaryIcd
            ? {
                descricao: primaryIcd?.description,
                id: primaryIcd?.id,
                subCategoria: primaryIcd?.subcategory,
              }
            : null,
          cidAssociado: relatedIcd
            ? {
                descricao: relatedIcd?.description,
                id: relatedIcd?.id,
                subCategoria: relatedIcd?.subcategory,
              }
            : null,
          cidSecundario: secondaryIcd
            ? {
                descricao: secondaryIcd?.description,
                id: secondaryIcd?.id,
                subCategoria: secondaryIcd?.subcategory,
              }
            : secondaryIcd,
          codigoExame: onlyDigits(updateData.procedure.code),
          exameJustificativaObservacao: updateData.examJustify,
          nome: updateData.procedure.name,
          quantidade: +updateData.procedure.quantity,
          procedimentosSecundarios: updateData.secondaryProcedures
            .filter((procedure) => {
              return !!procedure.code || !!procedure.name || !!procedure.quantity;
            })
            .map((procedure) => ({
              nome: procedure.name,
              codigoExame: onlyDigits(procedure.code),
              quantidade: procedure.quantity,
            })),
          tipoExameFormulario: {
            Chave: updateData.formType.key,
            idTipoExameFormulario: updateData.formType.id,
            Nome: updateData.formType.name,
          },
        } as ApacBpaApiPrescriptionPayload;

        prevPayloadExam[position] = data;

        const newData = {
          ...prev,
          formPayload: { ...(prev.formPayload as ApacBpaApiPayload), exameFormularioPrescricao: prevPayloadExam },
          prescriptionForm: prevForm,
        };
        // PrescriptionService.updateApacBpaForm(prescriptionId, newData.formPayload);

        return newData;
      } catch (error) {
        logger.error(error);
        return prev;
      }
    });
  },
  updateCompletedForm: async (prescriptionId: number) => {
    try {
      const payload = get().formPayload as ApacBpaApiPayload;
      const response = await PrescriptionService.updateApacBpaForm(prescriptionId, payload);
      set((prev) => {
        return { ...prev, completedForm: true };
      });
      return response;
    } catch (error) {
      logger.error(error);
      return null;
    }
  },
  resetApacBpaForm: () => {
    set((prev) => {
      return {
        ...prev,
        ...GET_EMPTY_STORE(),
      };
    });
  },
}));
